import React, { useRef, useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Text,
  StatusBar,
  Platform,
} from "react-native";
import {
  WelcomeSlideNew1,
  WelcomeSlideNew2,
  WelcomeSlideNew3,
  WelcomeSlideNew4,
} from "@/components/welcome";
import { Redirect, router } from "expo-router";
import Constants from "expo-constants";
import Icons from "@/assets/icons";
import { ActionButton } from "@/components/ActionButton";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";

const width = SCREEN_WIDTH;

// Total slides: 1 splash + 3 content slides = 4
const TOTAL_SLIDES = 4;

export default function WelcomeScreen() {
  const scrollRef = useRef<ScrollView>(null);
  const [page, setPage] = useState(0);
  const progress = useSharedValue(0);

  // Track welcome screen viewed on mount
  useEffect(() => {
    FBTrackingService.trackEvent(TRACKING_EVENTS.WELCOME_SCREEN_VIEWED, {
      eventData: {
        total_slides: TOTAL_SLIDES,
      },
    });

    // Track first slide viewed
    FBTrackingService.trackEvent(TRACKING_EVENTS.WELCOME_SLIDE_VIEWED, {
      eventData: {
        slide_number: 1,
        total_slides: TOTAL_SLIDES,
      },
    });
  }, []);

  // Animate progress bar and auto next
  useEffect(() => {
    progress.value = withTiming(1, { duration: 5000 }, (finished) => {
      if (finished && page < TOTAL_SLIDES - 1) {
        runOnJS(scrollToNext)();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const scrollToNext = () => {
    scrollRef.current?.scrollTo({ x: (page + 1) * width, animated: true });
  };

  const onScroll = (e: any) => {
    const x = e.nativeEvent.contentOffset.x;
    const newPage = Math.round(x / width);
    if (newPage !== page) {
      setPage(newPage);
      progress.value = 0;

      // Track slide viewed
      FBTrackingService.trackEvent(TRACKING_EVENTS.WELCOME_SLIDE_VIEWED, {
        eventData: {
          slide_number: newPage + 1,
          total_slides: TOTAL_SLIDES,
        },
      });
    }
  };

  const handleGetStarted = () => {
    FBTrackingService.trackEvent(TRACKING_EVENTS.GET_STARTED_CLICKED, {
      eventData: {
        from_slide: page + 1,
      },
    });
    router.navigate("/signin");
  };

  const handleSignUp = () => {
    FBTrackingService.trackEvent(TRACKING_EVENTS.GET_STARTED_CLICKED, {
      eventData: {
        from_slide: page + 1,
        direct_signup: true,
      },
    });
    router.navigate("/signup?startAt=15");
  };

  // Animated style for top progress bars (slides 2-4)
  // page 1, 2, 3 map to progress bar index 0, 1, 2
  const topProgressBarStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }));

  if (Platform.OS === "web") {
    return <Redirect href="/" />;
  }

  // Check if we're on the first slide (splash)
  const isFirstSlide = page === 0;

  return (
    <View style={styles.background}>
      <StatusBar barStyle="dark-content" />

      {/* Header: Only show on slides 2-4 */}
      {!isFirstSlide && (
        <View style={styles.header}>
          <View style={styles.progressBarWrap}>
            {[0, 1, 2].map((idx) => {
              const slideIndex = idx + 1; // Maps to page 1, 2, 3
              return (
                <View key={idx} style={styles.progressBarBg}>
                  {page === slideIndex && (
                    <Animated.View
                      style={[styles.progressBarActive, topProgressBarStyle]}
                    />
                  )}
                  {page > slideIndex && <View style={styles.progressBarActiveFull} />}
                </View>
              );
            })}
          </View>
          <View style={styles.logoRow}>
            <Icons.LogoWithTextBlack width={101} height={26} />
          </View>
        </View>
      )}

      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        style={styles.scroll}
        contentContainerStyle={isFirstSlide ? undefined : { paddingVertical: 16 }}
      >
        {/* Slide 1: Splash with centered logo and progress bar */}
        <View style={{ width }}>
          <WelcomeSlideNew1 progress={progress} />
        </View>
        {/* Slide 2 */}
        <View style={{ width }}>
          <WelcomeSlideNew2 />
        </View>
        {/* Slide 3 */}
        <View style={{ width }}>
          <WelcomeSlideNew3 />
        </View>
        {/* Slide 4 */}
        <View style={{ width }}>
          <WelcomeSlideNew4 />
        </View>
      </ScrollView>

      {/* Actions - hide on first slide */}
      {!isFirstSlide && (
        <View style={styles.actions}>
          <ActionButton
            title="Get started"
            onPress={handleGetStarted}
            style={styles.getStartedBtn}
          />
          <TouchableOpacity onPress={handleSignUp}>
            <Text style={styles.signInText}>Sign up</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  background: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    paddingTop: Constants.statusBarHeight,
  },
  header: {
    width: "100%",
    paddingHorizontal: 16,
    justifyContent: "space-between",
    marginTop: 8,
    gap: 16,
  },
  progressBarWrap: {
    flexDirection: "row",
    gap: 4,
    marginBottom: 12,
    width: "100%",
  },
  progressBarBg: {
    flex: 1,
    height: 3,
    borderRadius: 100,
    backgroundColor: "rgba(22, 29, 20, 0.2)",
    overflow: "hidden",
  },
  progressBarActive: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "#161D14",
    borderRadius: 100,
    height: 3,
  },
  progressBarActiveFull: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: "#161D14",
    borderRadius: 100,
    height: 3,
  },
  logoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  signUpLink: {
    fontFamily: "Inter",
    fontWeight: "600",
    fontSize: 16,
    color: "#0F9129",
    letterSpacing: 0.2,
  },
  scroll: {
    flex: 1,
  },
  actions: {
    alignItems: "center",
    paddingHorizontal: 16,
    marginBottom: 32,
  },
  signInText: {
    color: "#161D14",
    fontSize: 15,
    fontWeight: "600",
    textAlign: "center",
    opacity: 0.7,
  },
  getStartedBtn: {
    marginBottom: 8,
    width: "100%",
    height: 58,
    borderRadius: 100,
  },
});
